import { SizeProp } from '@fortawesome/fontawesome-svg-core';
import { faPlus, faRemove } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import React, { ButtonHTMLAttributes, ReactElement, Ref } from 'react';
import VoxelTooltipShort, { VoxelTooltipShortProps } from '../Tooltip/VoxelTooltipShort';
import { VoxelAlertSeverity } from '../Alert/VoxelAlert';

export interface VoxelIconButton extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'title'> {
  icon?: ReactElement<FontAwesomeIconProps>;
  icon2?: ButtonIconType;
  tooltip?: string;
  tooltipProps?: Omit<VoxelTooltipShortProps, 'title' | 'children' | 'disabled' | 'wrapWithSpan'>;
  disabled?: boolean;
  color?: ButtonIconColor;
  size?: 'xxxs' | 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  containerType?: 'default' | 'form' | 'table';
  ref?: Ref<HTMLButtonElement>;
  textColorClass?: string;
  hoverTextColorClass?: string;
  children?: any;
  spinner?: boolean;
  effect?: boolean;
  active?: boolean;
  activeColor?: ButtonIconColor;
}

export type ButtonIconSize = 'xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type ButtonIconType = 'delete' | 'add';
export type ButtonIconColor =
  | 'primary'
  | 'white'
  | 'transparent'
  | 'indigo'
  | 'violet'
  | 'sky'
  | 'light-gray'
  | 'gray'
  | 'blue'
  | VoxelAlertSeverity;

const VoxelIconButton: React.FC<VoxelIconButton> = React.forwardRef<
  HTMLButtonElement,
  VoxelIconButton
>(
  (
    {
      onClick,
      onMouseDown,
      onMouseUp,
      onMouseOver,
      tooltip = '',
      disabled,
      icon,
      color = 'light-gray',
      size = 'sm',
      icon2,
      textColorClass,
      hoverTextColorClass,
      children,
      tooltipProps,
      containerType,
      spinner,
      effect,
      active,
      activeColor,
      className,
      ...otherProps
    },
    ref
  ) => {
    let Icon: React.ReactNode | null = null;
    if (icon2 === 'add') Icon = <FontAwesomeIcon icon={faPlus} />;
    if (icon2 === 'delete') Icon = <FontAwesomeIcon icon={faRemove} />;

    // ---- COLOR ----
    let textColor = '';
    let hoverTextColor = '';
    let hoverBgColor = '';
    let bgColor = '';
    let activeBgColor = '';
    let shadow = '';
    let hoverShadow = '';

    if (active && color === 'gray') color = 'blue';
    if (active && color === 'light-gray') color = 'gray';
    if (active && color === 'white') color = 'light-gray';
    if (active && activeColor) color = activeColor;
    if (color === 'primary') {
      bgColor = 'bg-primary-600';
      hoverBgColor = 'hover:bg-primary-600/90';
      activeBgColor = 'active:bg-primary-600';
      textColor = 'text-white';
      shadow = 'shadow-none';
      hoverShadow = 'hover:shadow-sm';
    }
    if (color === 'white') {
      bgColor = 'bg-transparent';
      hoverBgColor =
        containerType === 'table' ? 'hover:bg-voxel-gray-200' : 'hover:bg-voxel-gray-100';
      activeBgColor = 'active:bg-transparent';
      textColor = textColorClass ? textColorClass : 'text-voxel-blue-600';
      hoverTextColor = hoverTextColorClass ? hoverTextColorClass : 'hover:text-voxel-blue-400';
      shadow = 'shadow-none';
      hoverShadow = 'hover:shadow-none';
    }
    if (color === 'transparent') {
      bgColor = 'bg-transparent';
      hoverBgColor = 'hover:bg-white/20 ';
      activeBgColor = 'active:bg-white/30';
      textColor = textColorClass ? textColorClass : 'text-voxel-blue-600';
      hoverTextColor = hoverTextColorClass ? hoverTextColorClass : 'hover:text-voxel-blue-400';
      shadow = 'shadow-none';
      hoverShadow = 'hover:shadow-none';
    }
    if (color === 'light-gray') {
      bgColor = 'bg-slate-200/60';
      hoverBgColor = 'hover:bg-slate-200/90';
      activeBgColor = 'active:bg-voxel-gray-200/60';
      textColor = textColorClass ? textColorClass : 'text-voxel-gray-700';
      hoverTextColor = hoverTextColorClass ? hoverTextColorClass : 'hover:text-voxel-gray-600';
      shadow = 'shadow-none';
      hoverShadow = 'hover:shadow-sm';
    }
    if (color === 'gray') {
      bgColor = 'bg-slate-300/90';
      hoverBgColor = 'hover:bg-slate-300';
      activeBgColor = 'active:bg-voxel-gray-300';
      textColor = textColorClass ? textColorClass : 'text-voxel-gray-700';
      hoverTextColor = hoverTextColorClass ? hoverTextColorClass : 'hover:text-voxel-gray-600';
      shadow = 'shadow-none';
      hoverShadow = 'hover:shadow-sm';
    }
    if (color === 'indigo') {
      bgColor = 'bg-indigo-500/90';
      hoverBgColor = 'hover:bg-indigo-500';
      activeBgColor = 'active:bg-indigo-500/90';
      textColor = 'text-white';
      shadow = 'shadow-none';
      hoverShadow = 'hover:shadow-sm';
    }
    if (color === 'violet') {
      bgColor = 'bg-violet-600/80';
      hoverBgColor = 'hover:bg-violet-600/90';
      activeBgColor = 'active:bg-violet-600/80';
      textColor = 'text-white';
      shadow = 'shadow-none';
      hoverShadow = 'hover:shadow-sm';
    }
    if (color === 'sky') {
      bgColor = 'bg-sky-500/80';
      hoverBgColor = 'hover:bg-sky-500/90';
      activeBgColor = 'active:bg-sky-500/80';
      textColor = 'text-white';
      shadow = 'shadow-none';
      hoverShadow = 'hover:shadow-sm';
    }
    if (color === 'error') {
      bgColor = 'bg-error-500/90';
      hoverBgColor = 'hover:bg-error-500';
      activeBgColor = 'active:bg-error-500/90';
      textColor = 'text-white';
      shadow = 'shadow-none';
      hoverShadow = 'hover:shadow-sm';
    }
    if (color === 'warning') {
      bgColor = 'bg-yellow-600/60';
      hoverBgColor = 'hover:bg-yellow-600/70';
      activeBgColor = 'active:bg-yellow-600/60';
      textColor = 'text-white';
      shadow = 'shadow-none';
      hoverShadow = 'hover:shadow-sm';
    }
    if (color === 'success') {
      bgColor = 'bg-emerald-600/60';
      hoverBgColor = 'hover:bg-emerald-600/70';
      activeBgColor = 'active:bg-emerald-600/60';
      textColor = 'text-white';
      shadow = 'shadow-none';
      hoverShadow = 'hover:shadow-sm';
    }
    if (color === 'blue') {
      bgColor = 'bg-slate-600';
      hoverBgColor = 'hover:bg-slate-600/90';
      activeBgColor = 'active:bg-slate-600';
      textColor = 'text-white';
      shadow = 'shadow-none';
      hoverShadow = 'hover:shadow-sm';
    }
    if (disabled) {
      bgColor = 'bg-voxel-gray-100';
      hoverBgColor = '';
      activeBgColor = '';
      shadow = 'shadow-none';
      textColor = 'text-voxel-gray-400';
      hoverTextColor = '';
    }
    if (color === 'white' && disabled) {
      bgColor = 'bg-transparent';
      hoverBgColor = '';
      activeBgColor = '';
      textColor = 'text-voxel-gray-300';
      hoverTextColor = '';
    }
    if (active) {
      hoverBgColor = '';
      hoverTextColor = '';
      tooltip = '';
      activeBgColor = '';
    }

    // ---- SIZE ----
    let sizingVar = '';
    let iconSize: SizeProp;
    if (size === 'xxs') {
      sizingVar = `w-4 h-4 p-2.5`;
    }
    if (size === 'xs') {
      sizingVar = `w-5 h-5 p-3`;
    }
    if (size === 'sm') {
      sizingVar = `w-6 h-6 p-3.5`;
    }
    if (size === 'md') {
      sizingVar = `w-7 h-7 p-4`;
    }
    if (size === 'lg') {
      sizingVar = `w-8 h-8 p-5`;
    }
    if (size === 'xl') {
      sizingVar = `w-9 h-9 p-6`;
    }

    return (
      <VoxelTooltipShort message={tooltip} {...tooltipProps} showOnFocus={false}>
        <button
          ref={ref}
          onClick={!disabled ? onClick : undefined}
          onMouseDown={!disabled ? onMouseDown : undefined}
          onMouseUp={!disabled ? onMouseUp : undefined}
          onMouseOver={!disabled ? onMouseOver : undefined}
          type={otherProps.type ?? 'button'}
          className={`
            ${!disabled && 'cursor-pointer'}
            ${disabled && 'cursor-default'}
            select-none
            inline-block
            rounded-full
            ${sizingVar}
            ${bgColor} ${effect ? 'animate-pulse' : ''}
            ${hoverBgColor}
            ${activeBgColor}
            ${shadow} ${hoverShadow}
            font-sans text-sm font-semibold leading-normal capitalize ${textColor} ${hoverTextColor}
            flex items-center justify-center
            ${spinner ? 'relative' : ''}
            ${className}
            
          `}
          {...otherProps}
        >
          {spinner && color === 'error' && (
            <div
              className="absolute top-0 bottom-0 left-0 right-0 
                            rounded-full animate-spin border-[2px] 
                            border-t-error-500 
                            border-l-transparent border-b-transparent border-r-transparent"
            />
          )}
          {spinner && color === 'white' && (
            <div
              className="absolute top-0 bottom-0 left-0 right-0 
                            rounded-full animate-spin border-[2px] 
                            border-t-gray-600 
                            border-l-transparent border-b-transparent border-r-transparent"
            />
          )}
          {spinner && color === 'blue' && (
            <div
              className="absolute top-0 bottom-0 left-0 right-0 
                            rounded-full animate-spin border-[2px] 
                            border-t-white/70 
                            border-l-transparent border-b-transparent border-r-transparent"
            />
          )}
          {spinner && color === 'warning' && (
            <div
              className="absolute top-0 bottom-0 left-0 right-0 
                            rounded-full animate-spin border-[2px] 
                            border-t-warning-700/70 
                            border-l-transparent border-b-transparent border-r-transparent"
            />
          )}
          {!!icon && icon}
          {children}
        </button>
      </VoxelTooltipShort>
    );
  }
);

export default VoxelIconButton;
